import React from "react";

import { Button } from "@/components/button";
import { Checkbox } from "@/components/checkbox";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
} from "@/components/dialog";
import { Modal } from "@/components/modal";
import { Text } from "@/components/text";

interface BulkDuplicateModalProps {
  isLoading?: boolean;
  isOpen: boolean;
  onConfirm: (options: {
    includeAttachments: boolean;
    includeCollaborators: boolean;
  }) => void;
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
  selectedCount: number;
}

export function BulkDuplicateModal({
  isLoading = false,
  isOpen,
  onConfirm,
  onOpenChange,
  selectedCount,
}: BulkDuplicateModalProps) {
  const [includeAttachments, setIncludeAttachments] = React.useState(false);
  const [includeCollaborators, setIncludeCollaborators] = React.useState(false);

  const handleConfirm = () => {
    onConfirm({
      includeAttachments,
      includeCollaborators,
    });
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      // Reset to defaults when closing
      setIncludeAttachments(false);
      setIncludeCollaborators(false);
    }
    onOpenChange(open);
  };

  return (
    <Modal
      classNames={{ modal: "w-[500px]" }}
      isDismissable
      isOpen={isOpen}
      onOpenChange={handleClose}
    >
      <Dialog>
        <DialogHeader>
          Duplicate {selectedCount} Request{selectedCount === 1 ? "" : "s"}
        </DialogHeader>
        <DialogCloseButton />
        <DialogBody>
          <div className="flex flex-col gap-4">
            <Text className="text-text-secondary">
              Choose what to include in the duplicated requests:
            </Text>

            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  isSelected={includeAttachments}
                  onChange={setIncludeAttachments}
                />
                <div className="flex flex-col">
                  <Text className="text-sm font-medium">
                    Include attachments
                  </Text>
                  <Text className="text-xs text-text-tertiary">
                    Copy all files and assets from the original requests
                  </Text>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  isSelected={includeCollaborators}
                  onChange={setIncludeCollaborators}
                />
                <div className="flex flex-col">
                  <Text className="text-sm font-medium">
                    Include collaborators
                  </Text>
                  <Text className="text-xs text-text-tertiary">
                    Add the same collaborators to the duplicated requests
                  </Text>
                </div>
              </label>
            </div>
          </div>
        </DialogBody>
        <DialogFooter className="flex gap-3 mt-6">
          <Button
            className="flex-1"
            color="gray"
            onPress={() => handleClose(false)}
            size="lg"
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            className="flex-1"
            color="color"
            isLoading={isLoading}
            onPress={handleConfirm}
            size="lg"
            variant="primary"
          >
            Duplicate Request{selectedCount === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
